import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "../../../utils/translation";
import {
  createDigitalCard,
  updateDigitalCard,
  listDigitalCards,
  selectDigitalCardsCreateError,
  selectDigitalCardsCreateLoading,
  selectDigitalCardsCreateMessage,
} from "../digitalCardsSlice";
import digitalCardsService from "../digitalCardsService";

const DigitalCardsCreatePage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const createLoading = useSelector(selectDigitalCardsCreateLoading);
  const adminAccess = useSelector((state) => state.auth?.adminAccess);
  const createError = useSelector(selectDigitalCardsCreateError);
  const createMessage = useSelector(selectDigitalCardsCreateMessage);
  const [successMessage, setSuccessMessage] = useState("");
  const [permissionError, setPermissionError] = useState("");
  const [formState, setFormState] = useState({
    fullName: "",
    email: "",
    phone: "",
    title: "",
    company: "",
    isActive: true,
    imagebase64: "",
  });
  const [imagePreview, setImagePreview] = useState("");

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    if (!adminAccess) {
      setPermissionError(t("admin_common.permission_denied"));
      return;
    }
    setSuccessMessage("");
    setPermissionError("");
    const payload = {
      fullName: formState.fullName?.trim() || "",
      email: formState.email?.trim() || "",
      phone: formState.phone?.trim() || "",
      title: formState.title?.trim() || "",
      company: formState.company?.trim() || "",
      isActive: Boolean(formState.isActive),
    };
    if (!payload.fullName) return;
    try {
      const created = await dispatch(createDigitalCard(payload)).unwrap();
      if (created?.id && formState.imagebase64) {
        const raw = formState.imagebase64.startsWith("data:")
          ? formState.imagebase64.split(",")[1]
          : formState.imagebase64;
        await digitalCardsService.updateDigitalCardImage(created.id, raw);
        await dispatch(updateDigitalCard({ id: created.id, data: { photo: [raw] } }));
      }
      await dispatch(listDigitalCards());
      setFormState({
        fullName: "",
        email: "",
        phone: "",
        title: "",
        company: "",
        isActive: true,
        imagebase64: "",
      });
      setImagePreview("");
      setSuccessMessage(t("admin_digital_cards_pages.create_success_message"));
    } catch (error) {
      // Error handled in slice
    }
  };

  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-body">
            <h4 className="card-title">{t("admin_digital_cards_pages.create_title")}</h4>
            {permissionError && (
              <div className="alert alert-danger">{permissionError}</div>
            )}
            {successMessage && (
              <div className="alert alert-success">{successMessage}</div>
            )}
            {createError && (
              <div className="text-danger mb-2">
                {createMessage || t("admin_digital_cards_pages.create_failed")}
              </div>
            )}
            <div className="form-group mb-3">
              <label className="text-black font-w500">{t("admin_common.name")}</label>
              <input
                type="text"
                className="form-control"
                name="fullName"
                value={formState.fullName}
                onChange={handleChange}
                placeholder={t("admin_common.name")}
              />
            </div>
            <div className="form-group mb-3">
              <label className="text-black font-w500">{t("admin_common.email")}</label>
              <input
                type="email"
                className="form-control"
                name="email"
                value={formState.email}
                onChange={handleChange}
                placeholder={t("admin_common.email")}
              />
            </div>
            <div className="form-group mb-3">
              <label className="text-black font-w500">{t("admin_common.phone")}</label>
              <input
                type="text"
                className="form-control"
                name="phone"
                value={formState.phone}
                onChange={handleChange}
                placeholder={t("admin_common.phone")}
              />
            </div>
            <div className="form-group mb-3">
              <label className="text-black font-w500">{t("admin_digital_cards_pages.title_label")}</label>
              <input
                type="text"
                className="form-control"
                name="title"
                value={formState.title}
                onChange={handleChange}
                placeholder={t("admin_digital_cards_pages.title_label")}
              />
            </div>
            <div className="form-group mb-3">
              <label className="text-black font-w500">{t("admin_digital_cards_pages.company_label")}</label>
              <input
                type="text"
                className="form-control"
                name="company"
                value={formState.company}
                onChange={handleChange}
                placeholder={t("admin_digital_cards_pages.company_label")}
              />
            </div>
            <div className="form-check form-switch mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="card-create-active"
                name="isActive"
                checked={Boolean(formState.isActive)}
                onChange={handleChange}
              />
              <label className="form-check-label" htmlFor="card-create-active">
                {formState.isActive ? t("admin_common.status_active") : t("admin_common.status_passive")}
              </label>
            </div>
            <div className="form-group mb-3">
              <label className="text-black font-w500">{t("admin_digital_cards_pages.photo_label")}</label>
              {imagePreview ? (
                <div className="mb-2">
                  <img
                    src={imagePreview}
                    alt="Kartvizit"
                    style={{ width: "100%", maxHeight: 240, objectFit: "contain", borderRadius: 8 }}
                  />
                </div>
              ) : (
                <div className="text-muted mb-2">{t("admin_digital_cards_pages.image_empty")}</div>
              )}
              <input
                type="file"
                className="form-control"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => {
                    const result = reader.result;
                    if (typeof result === "string") {
                      setImagePreview(result);
                      setFormState((prev) => ({ ...prev, imagebase64: result }));
                    }
                  };
                  reader.readAsDataURL(file);
                }}
              />
            </div>
            <button type="button" className="btn btn-success" onClick={handleSubmit} disabled={createLoading}>
              {t("admin_common.save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DigitalCardsCreatePage;
